import { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import jacaiLogo from "../images/JACAiLogo.png";
import "../css/AboutJacai.css";

const AboutJacai = () => {
  const year = new Date().getFullYear();
  const [lang, setLang] = useState("en");

  const toggleLang = () => {
    setLang((prev) => (prev === "ro" ? "en" : "ro"));
  };

  useEffect(() => {
    document.title = lang === "ro" ? "Despre JACAi" : "About JACAi";
  }, [lang]);

  return (
    <>
      <div className="about-page">
        <div className="jacai-page">
          <Container className="jacai-container py-5">
            <Row className="justify-content-center">
              <Col lg={10}>
                <div className="jacai-shell">
                  <button
                    type="button"
                    className="jacai-lang-toggle"
                    onClick={toggleLang}
                    aria-label={
                      lang === "ro" ? "Switch to English" : "Schimbă în română"
                    }
                  >
                    <span className={lang === "ro" ? "active" : ""}>RO</span>
                    <span className={lang === "en" ? "active" : ""}>EN</span>
                  </button>

                  <section className="jacai-hero">
                    <div className="jacai-hero-text">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <img
                          src={jacaiLogo}
                          alt="JACAi Logo"
                          className="jacai-hero-logo"
                        />
                        <span className="jacai-pill">Just A Chill AI</span>
                      </div>

                      <h1 className="jacai-hero-title">
                        {lang === "ro"
                          ? "Un chatbot experimental pentru elevi, studenți, profesori și oameni curioși - plus un Resume (CV) Analyzer"
                          : "An experimental chatbot for students, teachers, and curious people - plus a Resume (CV) Analyzer"}
                      </h1>

                      <p className="jacai-hero-subtitle">
                        {lang === "ro"
                          ? "JACAi este un asistent AI relaxat, cu o bază de cunoștințe relativ mică. E bun la explicații și structurarea ideilor, dar nu știe tot, poate să se încurce și uneori poate inventa detalii. Pe lângă chat, include și un modul de analiză a CV-ului (Resume Analyzer) pentru feedback structurat."
                          : "JACAi is a chill AI assistant with a relatively small knowledge base. It’s good at explanations and structuring ideas, but it doesn’t know everything, can get confused, and sometimes may invent details. Besides chat, it also includes a Resume (CV) Analyzer that provides structured feedback."}
                      </p>

                      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                        <Button as={Link} to="/" className="jacai-cta">
                          {lang === "ro"
                            ? "Deschide JACAi (chat + Resume Analyzer)"
                            : "Open JACAi (chat + Resume Analyzer)"}
                        </Button>
                        <span className="jacai-cta-caption">
                          {lang === "ro"
                            ? "Fără cont • deschizi chat-ul și începi"
                            : "No account • just open and start"}
                        </span>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <Badge>{lang === "ro" ? "Elevi" : "School students"}</Badge>
                        <Badge>
                          {lang === "ro" ? "Studenți" : "University students"}
                        </Badge>
                        <Badge>{lang === "ro" ? "Profesori" : "Teachers"}</Badge>
                        <Badge>
                          {lang === "ro"
                            ? "Profesioniști & self-learners"
                            : "Professionals & self-learners"}
                        </Badge>
                        <Badge>
                          {lang === "ro" ? "Resume (CV) feedback" : "Resume (CV) feedback"}
                        </Badge>
                      </div>
                    </div>

                    <div className="jacai-hero-visual">
                      <div className="jacai-chat-window">
                        <div className="jacai-chat-header">
                          <span className="jacai-chat-dot" />
                          <span className="jacai-chat-dot" />
                          <span className="jacai-chat-dot" />
                          <span className="jacai-chat-title">JACAi • live</span>
                        </div>
                        <div className="jacai-chat-body">
                          <div className="jacai-chat-bubble user">
                            {lang === "ro"
                              ? "Poți să-mi explici relativitatea „ca pentru liceu”?"
                              : 'Can you explain relativity “as if I’m in high school”?'}
                          </div>
                          <div className="jacai-chat-bubble bot">
                            {lang === "ro"
                              ? "Sigur. Gândește-te la spațiu și timp ca la o singură „țesătură”. Când pui ceva foarte greu pe ea (de exemplu o stea), țesătura se curbează, iar asta noi percepem ca gravitație."
                              : "Sure. Think of space and time as a single “fabric”. When you place something very heavy on it (like a star), that fabric bends, and what we feel as gravity is just things following that curve."}
                          </div>
                          <div className="jacai-chat-bubble user">
                            {lang === "ro"
                              ? "Și poți să-mi dai și un exemplu din viața reală?"
                              : "Can you give me a real-life example?"}
                          </div>
                          <div className="jacai-chat-bubble bot">
                            {lang === "ro"
                              ? "Un exemplu simplu este GPS-ul de pe telefon. Ca să fie precis, trebuie să țină cont de efectele relativității asupra timpului sateliților."
                              : "A simple example is the GPS in your phone. To stay accurate, it has to correct for relativity because satellites experience time slightly differently."}
                          </div>

                          <div className="jacai-chat-bubble user">
                            {lang === "ro"
                              ? "Poți să-mi analizezi CV-ul pentru un rol de Frontend?"
                              : "Can you review my resume for a Frontend role?"}
                          </div>
                          <div className="jacai-chat-bubble bot">
                            {lang === "ro"
                              ? "Da. Încarcă PDF-ul și spune-mi rolul țintă. Îți pot oferi feedback pe structură, bullet points, cuvinte-cheie și sugestii de îmbunătățire."
                              : "Yes. Upload the PDF and tell me your target role. I can give feedback on structure, bullet points, keywords, and improvements."}
                          </div>
                        </div>

                        <div className="jacai-chat-input">
                          <span className="jacai-chat-input-placeholder">
                            {lang === "ro"
                              ? "Întreabă ceva sau încarcă un CV (PDF)..."
                              : "Ask a question or upload a resume (PDF)..."}
                          </span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="jacai-section">
                    <h2 className="jacai-section-title">
                      {lang === "ro" ? "Cum te poate ajuta JACAi?" : "How can JACAi help you?"}
                    </h2>
                    <Row className="gy-3">
                      <Col md={6}>
                        <Card className="jacai-card">
                          <Card.Body>
                            <h3>{lang === "ro" ? "Pentru elevi" : "For school students"}</h3>
                            <p>
                              {lang === "ro"
                                ? "Explică lecțiile în cuvinte simple și te ajută să legi conceptele între ele. Poți cere reformulări pe nivel („explică-mi ca pentru clasa a 9-a”) sau exemple suplimentare."
                                : "Explains lessons in simple language and helps you connect concepts. You can ask for explanations at a certain level (“explain this as if I’m in 9th grade”) or extra examples."}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col md={6}>
                        <Card className="jacai-card">
                          <Card.Body>
                            <h3>
                              {lang === "ro" ? "Pentru studenți" : "For university students"}
                            </h3>
                            <p>
                              {lang === "ro"
                                ? "Te ajută cu rezumate, structurarea proiectelor, idei pentru prezentări și clarificări teoretice. Poate sugera direcții de explorat, dar nu înlocuiește cercetarea serioasă."
                                : "Helps with summaries, project structure, presentation ideas, and theory clarifications. It can suggest directions to explore, but it doesn’t replace real research."}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col md={6}>
                        <Card className="jacai-card">
                          <Card.Body>
                            <h3>{lang === "ro" ? "Pentru profesori" : "For teachers"}</h3>
                            <p>
                              {lang === "ro"
                                ? "Sugerează explicații alternative, idei de exemple și întrebări de verificare. Îl poți folosi ca punct de pornire, apoi adaptezi tu conținutul pentru elevii sau studenții tăi."
                                : "Suggests alternative explanations, example ideas, and check questions. You can use it as a starting point and then adapt everything for your class."}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col md={6}>
                        <Card className="jacai-card">
                          <Card.Body>
                            <h3>
                              {lang === "ro"
                                ? "Resume (CV) Analyzer"
                                : "Resume (CV) Analyzer"}
                            </h3>
                            <p>
                              {lang === "ro"
                                ? "Încarci CV-ul în PDF și primești feedback structurat: claritate, structură, bullet points, cuvinte-cheie (ATS), lipsuri, plus sugestii de îmbunătățire în funcție de rolul țintă."
                                : "Upload your resume as a PDF and get structured feedback: clarity, structure, bullet points, keywords (ATS), missing sections, and improvements tailored to your target role."}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </section>

                  <section className="jacai-section jacai-section-grid">
                    <div>
                      <h2 className="jacai-section-title">
                        {lang === "ro" ? "Cum funcționează?" : "How does it work?"}
                      </h2>
                      <ol className="jacai-list">
                        <li>
                          <strong>
                            {lang === "ro"
                              ? "Pui întrebarea normal, ca într-un chat."
                              : "You ask your question like in any chat."}
                          </strong>{" "}
                          {lang === "ro"
                            ? "Nu ai nevoie de prompturi perfecte sau formulări speciale."
                            : "No need for perfect prompts or special syntax."}
                        </li>
                        <li>
                          <strong>
                            {lang === "ro"
                              ? "Pentru CV, încarci un PDF și opțional menționezi rolul țintă."
                              : "For resumes, you upload a PDF and optionally specify your target role."}
                          </strong>{" "}
                          {lang === "ro"
                            ? "JACAi îți oferă feedback pe structură și conținut."
                            : "JACAi provides structured feedback on content and structure."}
                        </li>
                        <li>
                          <strong>
                            {lang === "ro"
                              ? "JACAi combină un model de limbaj cu un RAG (retrieval) mic."
                              : "JACAi combines a language model with a small retrieval layer (RAG)."}
                          </strong>{" "}
                          {lang === "ro"
                            ? "În unele cazuri folosește fragmente dintr-o bază de cunoștințe curatoriată, dar acoperirea este limitată."
                            : "In some cases it uses snippets from a curated knowledge base, but coverage is limited."}
                        </li>
                        <li>
                          <strong>
                            {lang === "ro"
                              ? "Când nu găsește ceva clar, poate „ghici”."
                              : "When it can’t find something clearly, it may “guess” a plausible answer."}
                          </strong>{" "}
                          {lang === "ro"
                            ? "Răspunsul poate suna bine, dar poate fi greșit. Verifică informațiile importante."
                            : "The answer can sound good but be wrong. Double-check important information."}
                        </li>
                      </ol>
                    </div>

                    <div>
                      <h2 className="jacai-section-title">
                        {lang === "ro" ? "Surse & transparență" : "Sources & transparency"}
                      </h2>
                      <p>
                        {lang === "ro"
                          ? "Baza de cunoștințe folosită de JACAi este relativ mică și este în continuă schimbare. Include și text derivat din Wikipedia, împărțit în bucăți tematice, pentru a ancora mai bine anumite răspunsuri."
                          : "The knowledge base JACAi uses is relatively small and constantly changing. It includes text derived from Wikipedia, split into thematic chunks, to better ground some of the answers."}
                      </p>
                      <p className="mb-2">
                        {lang === "ro"
                          ? "Conținutul Wikipedia® este disponibil sub licența Creative Commons Attribution-ShareAlike, iar JACAi nu își asumă proprietatea asupra acestuia."
                          : "Wikipedia® content is available under the Creative Commons Attribution-ShareAlike License, and JACAi does not claim ownership of it."}
                      </p>
                      <p className="jacai-note">
                        {lang === "ro"
                          ? "Tratează JACAi ca pe un instrument de brainstorming și explicații, nu ca pe o autoritate absolută."
                          : "Treat JACAi as a tool for brainstorming and explanations, not as an absolute authority."}
                      </p>
                    </div>
                  </section>

                  <section className="jacai-section jacai-section-grid">
                    <div>
                      <h2 className="jacai-section-title">
                        {lang === "ro" ? "Limitări importante" : "Important limitations"}
                      </h2>
                      <ul className="jacai-list">
                        <li>
                          {lang === "ro"
                            ? "Poate să înțeleagă greșit întrebarea sau contextul și să dea un răspuns care sună bine, dar nu e corect."
                            : "It can misunderstand your question or the context and produce an answer that sounds good but is wrong."}
                        </li>
                        <li>
                          {lang === "ro"
                            ? "Poate amesteca informații despre persoane diferite atunci când numele seamănă. Dacă vezi o „biografie” pentru cineva care nu există, trateaz-o ca pe ficțiune."
                            : "It can mix information about different people when names are similar. If you see a “biography” for someone who doesn’t exist, treat it as fiction."}
                        </li>
                        <li>
                          {lang === "ro"
                            ? "Analiza CV-ului este feedback orientativ. Nu garantează angajarea și nu înlocuiește un recruiter sau un specialist."
                            : "Resume analysis is informational feedback. It doesn’t guarantee hiring and doesn’t replace a recruiter or a professional reviewer."}
                        </li>
                        <li>
                          {lang === "ro"
                            ? "Răspunsurile juridice, medicale sau financiare sunt strict informative și nu înlocuiesc un specialist real."
                            : "Legal, medical, or financial answers are informational only and do not replace a real professional."}
                        </li>
                        <li>
                          {lang === "ro"
                            ? "Nu are o acoperire completă a tuturor domeniilor și nu este mereu la zi."
                            : "It doesn’t cover all domains and is not always up to date."}
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h2 className="jacai-section-title">
                        {lang === "ro" ? "Date & confidențialitate" : "Data & privacy"}
                      </h2>
                      <p>
                        {lang === "ro"
                          ? "JACAi stochează mesajele pe server și le leagă de un identificator anonim. Asta ajută la aplicarea limitelor zilnice, păstrarea unui istoric de bază al conversației și îmbunătățirea asistentului în timp."
                          : "JACAi stores your messages on the server and links them to an anonymous identifier. This helps enforce daily limits, keep a basic conversation history, and improve the assistant over time."}
                      </p>
                      <p>
                        {lang === "ro"
                          ? "Dacă folosești Resume Analyzer, fișierul încărcat (CV-ul) este procesat pentru a genera analiza și poate fi stocat pentru scurt timp în scopuri operaționale (de exemplu, depanare sau îmbunătățire). Evită să încarci informații sensibile dacă nu este necesar."
                          : "If you use the Resume Analyzer, the uploaded file (your resume/CV) is processed to generate the analysis and may be stored for a short period for operational purposes (e.g., debugging or improvement). Avoid uploading sensitive information if it’s not necessary."}
                      </p>
                      <p>
                        {lang === "ro"
                          ? "Pentru detalii complete, consultă "
                          : "For full details, see "}
                        <Link to="/privacy-policy">
                          {lang === "ro"
                            ? "Politica de confidențialitate"
                            : "Privacy Policy"}
                        </Link>{" "}
                        {lang === "ro" ? "și " : "and "}
                        <Link to="/terms-of-use">
                          {lang === "ro" ? "Termenii de utilizare" : "Terms of Use"}
                        </Link>
                        .
                      </p>
                    </div>
                  </section>

                  <section className="jacai-section">
                    <h2 className="jacai-section-title">
                      {lang === "ro" ? "Cine a creat JACAi?" : "Who built JACAi?"}
                    </h2>
                    <p className="mb-0">
                      {lang === "ro"
                        ? "JACAi este un proiect personal creat de "
                        : "JACAi is a personal project built by "}
                      <strong>JACAi</strong>
                      {lang === "ro"
                        ? ", ca experiment în învățare asistată de AI, organizare de cunoaștere și design modern de chatbot."
                        : ", as an experiment in AI-assisted learning, knowledge organization, and modern chatbot design."}
                    </p>
                  </section>

                  <div className="jacai-bottom-bar">
                    <span>
                      © {year} JACAi •{" "}
                      {lang === "ro"
                        ? "Toate drepturile rezervate"
                        : "All rights reserved"}
                    </span>
                    <Link to="/">
                      {lang === "ro" ? "← Înapoi la chat" : "← Back to chat"}
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default AboutJacai;